/**
 * Banner 10 — Hero Banner (W3).
 *
 * Figma "Typography System · 4 Breakpoints" (qaf6Kz0KF0k4TEYbwQ0hGB):
 *   xs 412:2431 · sm/1080 412:1974 · md/1230 412:1796 · lg/1430 412:2154 · xl/1750 465:4194
 *   PRD/constraints 444:2508.
 *
 * Program-page hero: value-proposition content column (left) + inline LeadDrop9
 * lead form (right on desktop, CTA on mobile — embedded exactly like About 2).
 *
 * Metadata tables: metadata = eyebrow badges (1–3), metadata_1 = paragraph
 * bullets (exactly 3), metadata_2 = breadcrumbs (optional), metadata_3 = alumni
 * avatars (Trust Builder 1).
 */
const IMAGE_ACCEPT = "image/png,image/jpeg,image/webp,image/gif,image/svg+xml";

export const BANNER_10_CONSTANTS = {
  METADATA_TITLES: {
    metadata: "Eyebrow Badges (min 1, max 3 — optional logo per badge)",
    metadata_1:
      "Paragraph Bullets (exactly 3 — used when Paragraph variant = Bullets)",
    metadata_2: "Breadcrumbs (optional — shown when 'Show breadcrumbs' is on)",
    metadata_3: "Alumni Avatars (Trust Builder 1)",
  },

  // Per-group row-count limits enforced in the admin table editor.
  METADATA_LIMITS: {
    metadata: { min: 1, max: 3 },
    metadata_1: { min: 3, max: 3 },
    metadata_3: { min: 0, max: 6 },
  },

  // Eyebrow badge row
  DEFAULT_ROW: {
    id: "1",
    label: "UGC Entitled",
    logo: "",
    priority: 1,
    status: true,
  },
  // Paragraph bullet row
  DEFAULT_ROW_1: {
    id: "1",
    text: "MBA Degree + CFP® Global Certification",
    icon: "",
    priority: 1,
    status: true,
  },
  // Breadcrumb row
  DEFAULT_ROW_2: {
    id: "1",
    meta_label: "Home",
    meta_url: "/",
    meta_priority: 1,
    meta_status: true,
  },
  // Alumni avatar row
  DEFAULT_ROW_3: {
    id: "1",
    image: "",
    priority: 1,
    status: true,
  },

  BANNER_FIELDS_CONFIG: [
    // ── Widget background ────────────────────────────────────────────────
    {
      fieldName: "background_image",
      label: "Widget background image (desktop / tablet)",
      value: "",
      valueType: "file",
      filedType: "FILE",
      placeholder: "Upload full-bleed background (overlay baked into the image)",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      fieldName: "background_image_mobile",
      label: "Widget background image (mobile)",
      value: "",
      valueType: "file",
      filedType: "FILE",
      placeholder: "Optional — falls back to background colour if empty",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      fieldName: "background_color",
      label: "Background colour (fallback when no image)",
      value: "#50040B",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#50040B",
    },

    // ── Breadcrumbs ──────────────────────────────────────────────────────
    // One toggle per view: the strip (rows in the Breadcrumbs table) can be on
    // for desktop and off for mobile, or the other way round. The legacy shared
    // `show_breadcrumbs` column is intentionally NOT edited here any more — it
    // stays in the DB as the fallback for rows saved before the split.
    {
      fieldName: "show_breadcrumbs_desktop",
      label: "Show breadcrumbs on desktop / tablet (rows in the Breadcrumbs table)",
      value: false,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: false,
    },
    {
      fieldName: "show_breadcrumbs_mobile",
      label: "Show breadcrumbs on mobile (rows in the Breadcrumbs table)",
      value: false,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: false,
    },

    // ── Eyebrow badges ───────────────────────────────────────────────────
    // Mobile-only opt-out. Defaults ON: badges have always rendered on mobile,
    // so existing banners keep their look. Desktop is not gated.
    {
      fieldName: "show_eyebrow_badges_mobile",
      label: "Show eyebrow badges on mobile",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
    {
      fieldName: "eyebrow_badge_bg_color",
      label: "Eyebrow badge background colour",
      value: "rgba(255,255,255,0.14)",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "rgba(255,255,255,0.14)",
    },

    // ── Heading · Sub heading · Subtitle ─────────────────────────────────
    {
      fieldName: "title_1",
      label: "Heading (mandatory)",
      value: "India's 1st Online MBA in",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. India's 1st Online MBA in",
      required: true,
      defaultValue: "India's 1st Online MBA in",
    },
    {
      fieldName: "title_1_color",
      label: "Heading colour",
      value: "#F5F5F5",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#F5F5F5",
    },
    // Mobile-only opt-out; defaults ON so existing banners keep their look.
    {
      fieldName: "show_title_1_mobile",
      label: "Show heading on mobile",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
    {
      fieldName: "title_2",
      label: "Sub heading (mandatory)",
      value: "Product Management",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Product Management",
      required: true,
      defaultValue: "Product Management",
    },
    {
      fieldName: "title_2_color",
      label: "Sub heading colour",
      value: "#F5F5F5",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#F5F5F5",
    },
    // Mobile-only opt-out; defaults ON so existing banners keep their look.
    {
      fieldName: "show_title_2_mobile",
      label: "Show sub heading on mobile",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
    {
      fieldName: "subtitle",
      label: "Supporting line (below sub heading)",
      value: "with CFP® Global Certification program",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. with CFP® Global Certification program",
      defaultValue: "with CFP® Global Certification program",
    },
    {
      fieldName: "subtitle_color",
      label: "Supporting line colour",
      value: "rgba(255,255,255,0.8)",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "rgba(255,255,255,0.8)",
    },
    // Mobile-only opt-out; defaults ON so existing banners keep their look.
    {
      fieldName: "show_subtitle_mobile",
      label: "Show supporting line on mobile",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },

    // ── Mobile creative (mobile view only — not rendered on desktop) ──────
    // The full creative (headline, artwork, supporting text, corner treatment)
    // is baked into the uploaded image; nothing inside it is coded. 4:5 aspect,
    // rendered below the sub-heading. Shadow: show/hide + colour only — the
    // geometry is fixed in the widget.
    // NOTE: the PRD says mandatory; kept optional per product call (03 Sep 2026)
    // so banner saves are not blocked. The widget simply skips the block when
    // the upload is empty.
    {
      fieldName: "mobile_creative_image",
      label: "Mobile creative (4:5, full creative baked into the image)",
      value: "",
      valueType: "file",
      filedType: "FILE",
      placeholder: "Upload the 4:5 mobile creative",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      fieldName: "show_mobile_creative_shadow",
      label: "Show shadow on the mobile creative",
      value: false,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: false,
    },
    {
      fieldName: "mobile_creative_shadow_color",
      label: "Mobile creative shadow colour",
      value: "rgba(0,0,0,0.35)",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "rgba(0,0,0,0.35)",
    },

    // ── Paragraph (bullets / text) ───────────────────────────────────────
    // Mobile-only opt-out for the whole paragraph block (both variants).
    // Defaults ON: the block has always rendered on mobile, so existing
    // banners keep their look. Desktop is not gated.
    {
      fieldName: "show_paragraph_mobile",
      label: "Show paragraph / bullets on mobile",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
    {
      fieldName: "paragraph_variant",
      label: "Paragraph variant",
      value: "bullets",
      valueType: "string",
      filedType: "SELECT",
      placeholder: "Select paragraph variant",
      required: true,
      defaultValue: "bullets",
      className: "h-9 py-1.5 text-sm",
      iconClassName: "!size-4",
      options: [
        { label: "Bullets (exactly 3)", value: "bullets" },
        { label: "Paragraph (3-line max)", value: "paragraph" },
      ],
    },
    {
      fieldName: "paragraph_text",
      label: "Paragraph text (used when variant = Paragraph; max 3 lines)",
      value: "",
      valueType: "string",
      filedType: "TEXT_AREA",
      placeholder: "Enter supporting paragraph (clamped to 3 lines)",
      defaultValue: "",
    },
    {
      fieldName: "paragraph_color",
      label: "Paragraph / bullet text colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "bullet_icon",
      label: "Default bullet icon (optional — defaults to tick-circle)",
      value: "",
      valueType: "file",
      filedType: "FILE",
      placeholder: "Upload a shared bullet icon",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },

    // ── Collaboration ────────────────────────────────────────────────────
    {
      fieldName: "collaboration_image",
      label: "Collaboration image (desktop) — optional",
      value: "",
      valueType: "file",
      filedType: "FILE",
      placeholder: "Single image block (e.g. knowledge partners strip)",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      fieldName: "collaboration_image_mobile",
      label: "Collaboration image (mobile) — optional",
      value: "",
      valueType: "file",
      filedType: "FILE",
      placeholder: "Separate image for mobile view",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },

    // ── CTA (opens the lead drawer, or the CTA link when one is set) ─────
    {
      fieldName: "cta_action",
      label: "CTA click action",
      value: "download",
      valueType: "string",
      filedType: "SELECT",
      placeholder: "Select CTA action",
      required: false,
      defaultValue: "download",
      className: "h-9 py-1.5 text-sm",
      iconClassName: "!size-4",
      options: [
        { label: "Download (brochure form)", value: "download" },
        { label: "Apply (registration form)", value: "apply" },
      ],
    },
    {
      fieldName: "download_brochure_text",
      label: "CTA label",
      value: "Download Brochure",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Download Brochure",
      defaultValue: "Download Brochure",
    },
    {
      fieldName: "button_color",
      label: "CTA background colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "cta_text_color",
      label: "CTA text colour",
      value: "#222222",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#222222",
    },
    {
      fieldName: "explore_cta_url",
      label: "CTA link (overrides the click action above when set)",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder:
        "e.g. https://…, /page, or #section — empty = use the click action",
      defaultValue: "",
    },

    // ── Trust Builder 1 — alumni ─────────────────────────────────────────
    // One toggle per view. The legacy shared `show_learners` column is no longer
    // edited here — it stays in the DB as the fallback for rows saved before
    // the split (widget reads `show_learners_<view> ?? show_learners`).
    {
      fieldName: "show_learners_desktop",
      label: "Show Trust Builder 1 on desktop / tablet (alumni avatars + text)",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
    {
      fieldName: "show_learners_mobile",
      label: "Show Trust Builder 1 on mobile (alumni avatars + text)",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
    {
      fieldName: "learners_description",
      label: "Alumni text",
      value: "Join 10k+ alumni",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Join 10k+ alumni",
      defaultValue: "Join 10k+ alumni",
    },
    {
      fieldName: "learners_description_color",
      label: "Alumni text colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },

    // ── Trust Builder 2 — rating ─────────────────────────────────────────
    // Per-view pair; legacy shared `show_rating` stays as the DB fallback.
    {
      fieldName: "show_rating_desktop",
      label: "Show Trust Builder 2 on desktop / tablet (star rating)",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
    {
      fieldName: "show_rating_mobile",
      label: "Show Trust Builder 2 on mobile (star rating)",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
    {
      fieldName: "rating_number",
      label: "Rating (e.g. 4/5 — drives the filled stars)",
      value: "4/5",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. 4/5",
      defaultValue: "4/5",
    },
    {
      fieldName: "rating_number_color",
      label: "Rating value colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "rating_text",
      label: "Rating count text",
      value: "(7.8K ratings)",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. (7.8K ratings)",
      defaultValue: "(7.8K ratings)",
    },
    {
      fieldName: "rating_color",
      label: "Rating count text colour",
      value: "rgba(255,255,255,0.5)",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "rgba(255,255,255,0.5)",
    },

    // ── Lead form + flags ────────────────────────────────────────────────
    {
      fieldName: "enable_lead_form",
      label: "Enable inline lead form (uses the university's configured form)",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
    {
      fieldName: "enable_animation",
      label: "Enable scroll-reveal animation",
      value: false,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: false,
    },
    {
      fieldName: "status",
      label: "Banner Status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],

  // metadata — Eyebrow badges
  METADATA_FIELDS: [
    {
      header: "Badge label",
      fieldName: "label",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. UGC Entitled",
      defaultValue: "",
    },
    {
      header: "Badge logo / icon (optional)",
      fieldName: "logo",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload badge logo",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      header: "Priority",
      fieldName: "priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "Display order (lower first)",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable this badge",
      defaultValue: true,
    },
  ],

  // metadata_1 — Paragraph bullets (exactly 3)
  METADATA_FIELDS_1: [
    {
      header: "Bullet text",
      fieldName: "text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. MBA Degree + CFP® Global Certification",
      defaultValue: "",
    },
    {
      header: "Bullet icon (optional — overrides the default tick)",
      fieldName: "icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload bullet icon",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      header: "Priority",
      fieldName: "priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "Display order (lower first)",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable this bullet",
      defaultValue: true,
    },
  ],

  // metadata_2 — Breadcrumbs (optional)
  METADATA_FIELDS_2: [
    {
      header: "Label",
      fieldName: "meta_label",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Programs",
      defaultValue: "",
    },
    {
      header: "URL / path (leave empty for the current page)",
      fieldName: "meta_url",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. /programs",
      defaultValue: "",
    },
    {
      header: "Priority",
      fieldName: "meta_priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "Display order (lower first)",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "meta_status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable this crumb",
      defaultValue: true,
    },
  ],

  // metadata_3 — Alumni avatars (Trust Builder 1)
  METADATA_FIELDS_3: [
    {
      header: "Avatar image",
      fieldName: "image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload avatar",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      header: "Priority",
      fieldName: "priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "Display order (lower first)",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable this avatar",
      defaultValue: true,
    },
  ],
};
